import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { SlideContainer } from './container';
import { SlideContent } from './content';
import { Text } from './text';
import { Highlight } from './highlight';
import { SlideProps } from '../Recap';
import RecapSlide from '../RecapSlide';
import { asset } from '../../../utils/misc';

const PopCornAnimation1 = keyframes`
    0% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-150px) rotate(10deg);
    }
    100% {
        transform: translateY(0) rotate(359deg);
    }
`;

const PopCornAnimation2 = keyframes`
    0% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-110px) rotate(30deg);
    }
    100% {
        transform: translateY(0) rotate(359deg);
    }
`;

const PopCornAnimation3 = keyframes`
    0% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-130px) rotate(10deg);
    }
    100% {
        transform: translateY(0) rotate(359deg);
    }
`;

const PopCornAnimation4 = keyframes`
    0% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-170px) rotate(60deg);
    }
    100% {
        transform: translateY(0) rotate(359deg);
    }
`;

const PopCornImage = styled.img<{ index : number }>`
    position: absolute;
    bottom: -40px;
    right: ${props => props.index * 30}px;
    width: ${props => 30 + (props.index % 5)}px;
    animation: ${props => {
        switch (props.index) {
            case 0: return PopCornAnimation1;
            case 1: return PopCornAnimation2;
            case 2: return PopCornAnimation3;
            case 3: return PopCornAnimation4;
            case 4: return PopCornAnimation1;
            case 5: return PopCornAnimation2;
            case 6: return PopCornAnimation3;
            case 7: return PopCornAnimation4;
            case 8: return PopCornAnimation1;
            case 9: return PopCornAnimation2;
            case 10: return PopCornAnimation3;
            case 11: return PopCornAnimation4;
            case 12: return PopCornAnimation1;
            case 13: return PopCornAnimation2;
            case 14: return PopCornAnimation3;
        }
    }} ${props => {
        switch (props.index) {
            case 0: return 1.5;
            case 1: return 1;
            case 2: return 1.25;
            case 3: return 1.6;
            case 4: return 2.1;
            case 5: return 0.9;
            case 6: return 1.45;
            case 7: return 1.3;
            case 8: return 0.95;
            case 9: return 2;
            case 10: return 1.25;
            case 11: return 1.15;
            case 12: return 1.05;
            case 13: return 0.9;
            case 14: return 1.15;
        }
    }}s ease-in-out infinite;
    animation-delay: ${props => {
        switch (props.index) {
            case 0: return 1.5;
            case 1: return 1;
            case 2: return 1.25;
            case 3: return 1.6;
            case 4: return 1.1;
            case 5: return 0.9;
            case 6: return 1.45;
            case 7: return 1.3;
            case 8: return 0.95;
            case 9: return 1;
            case 10: return 1.25;
            case 11: return 1.15;
            case 12: return 1.05;
            case 13: return 0.9;
            case 14: return 1.15;
        }
    }}s;
`;

export const Slide3 = ({ current = false, scale, data, soundEnabled = false }: SlideProps) => {
    const musicUrl = asset('/dist/audio/recap-music-03.mp3');
    const soundUrl = asset('/dist/audio/popcorn-pop.mp3');
    const [primaryColor, setPrimaryColor] = React.useState('#139dd1');
    const [accentColor, setAccentColor] = React.useState('#139dd1');
    const [secondaryColor, setSecondaryColor] = React.useState('#15485b');
    const [primaryFont, setPrimaryFont] = React.useState<'basis' | 'plain' | 'maison-neue' | 'fifties-cond-med' | 'pp'>('fifties-cond-med');
    const [secondaryFont, setSecondaryFont] = React.useState<'basis' | 'plain' | 'maison-neue' | 'fifties-cond-med' | 'pp'>('fifties-cond-med');
    const [line1Props, setLine1Props] = React.useState<any>({});
    const [line2Props, setLine2Props] = React.useState<any>({});
    const [line3Props, setLine3Props] = React.useState<any>({});
    const [line4Props, setLine4Props] = React.useState<any>({});
    const [line5Props, setLine5Props] = React.useState<any>({});
    const [line6Props, setLine6Props] = React.useState<any>({});

    useEffect(() => {
        if (!data) return;

        let concept = data.concept.toLowerCase();
        // concept = concept || 'tradicional';

        switch (concept) {
            case 'traditional':
            case 'tradicional':
                setPrimaryColor('#139dd1');
                setAccentColor('#139dd1');
                setSecondaryColor('#15485b');
                setPrimaryFont('fifties-cond-med');
                setSecondaryFont('fifties-cond-med');
                setLine1Props({
                    fontSize: 33,
                });
                setLine2Props({
                    fontSize: 53,
                });
                setLine3Props({
                    fontSize: 55,
                    fontWeight: 400
                });
                setLine4Props({
                    fontSize: 53,
                });
                setLine5Props({
                    fontSize: 59,
                });
                setLine6Props({
                    fontSize: 64,
                });
                break;
            case 'market':
                setPrimaryColor('#ffffff');
                setAccentColor('#dc3d25');
                setSecondaryColor('#000000');
                setPrimaryFont('plain');
                setSecondaryFont('basis');
                setLine1Props({
                    fontSize: 25,
                    fontWeight: 400,
                });
                setLine2Props({
                    fontSize: 25,
                    fontWeight: 700,
                });
                setLine3Props({
                    fontSize: 38,
                    fontWeight: 700,
                    textAlign: 'left',
                });
                setLine4Props({
                    fontSize: 19,
                    fontWeight: 700,
                });
                setLine5Props({
                    fontSize: 31,
                    fontWeight: 700,
                });
                setLine6Props({
                    fontSize: 17,
                    fontWeight: 400,
                });
                break;
            case 'platinum':
            case 'platino':
                setPrimaryColor('#c51c2f');
                setAccentColor('#c51c2f');
                setSecondaryColor('#63171a');
                setPrimaryFont('pp');
                setSecondaryFont('pp');
                setLine1Props({
                    fontSize: 25,
                    fontWeight: 400,
                });
                setLine2Props({
                    fontSize: 30,
                    fontWeight: 700,
                });
                setLine3Props({
                    fontSize: 40,
                    fontWeight: 700,
                });
                setLine4Props({
                    fontSize: 30,
                    fontWeight: 700,
                });
                setLine5Props({
                    fontSize: 46,
                    fontWeight: 700,
                });
                setLine6Props({
                    fontSize: 34,
                    fontWeight: 700,
                });
                break;
        }
    }, [data]);

    const sizeFactor = 1.3;

    return (
        <RecapSlide animations={[
            ['line-1'],
            ['line-2'],
            ['line-3'],
            ['line-4'],
            ['line-5'],
            ['line-6'],
        ]} focus={current} soundEnabled={soundEnabled} musicUrl={musicUrl} soundUrl={soundUrl}>
            <SlideContainer>
                <SlideContent bg={secondaryColor} scale={scale}>
                    <Text className="overflow-h line-1"
                        color={primaryColor}
                        fontFamily={primaryFont}
                        fontSize={`${line1Props.fontSize * sizeFactor}px`}
                        fontWeight={line1Props.fontWeight || 400}
                        lineHeight='1'
                    >
                        <span className="anim anim-slide-from-top">Tu</span>
                        {' '}
                        <span className="anim anim-slide-from-top">experiencia</span>
                    </Text>
                    <Text className="line-2"
                        color={primaryColor}
                        fontFamily={primaryFont}
                        fontSize={`${line2Props.fontSize * sizeFactor}px`}
                        fontWeight={line2Props.fontWeight || 400}
                        lineHeight='1'
                    >
                        <span className="anim anim-slide-from-top anim-fade-in" style={{ transform: "translateY(-620%)" }}>favorita</span>
                        {' '}
                        <span className="anim anim-slide-from-top anim-fade-in" style={{ transform: "translateY(-620%)" }}>es</span>
                    </Text>

                    <Highlight className="overflow-h anim anim-fade-in line-3"
                        bg={accentColor}
                        color={secondaryColor}
                        fontFamily={secondaryFont}
                        fontSize={`${line3Props.fontSize * sizeFactor}px`}
                        fontWeight={line3Props.fontWeight || 700}
                        lineHeight='1.2'
                        style={{
                            minWidth: '100%',
                            margin: `${8 * sizeFactor}px 0 ${16 * sizeFactor}px`,
                            minHeight: 'auto',
                            justifyContent: line3Props.textAlign || 'left'
                        }}
                    >
                        <span className="anim anim-slide-from-top">
                            {data && data.concept}
                        </span>
                    </Highlight>

                    <Text className="overflow-h wait line-4"
                        color={primaryColor}
                        fontFamily={primaryFont}
                        fontSize={`${line4Props.fontSize * sizeFactor}px`}
                        fontWeight={line4Props.fontWeight || 400}
                        lineHeight='1'
                        style={{ marginBottom: '3px' }}
                    >
                        <span className="anim anim-slide-from-above">Disfrutaste</span>
                    </Text>

                    <Highlight className="overflow-h box anim-fade-in line-5"
                        bg={accentColor}
                        color={secondaryColor}
                        fontFamily={secondaryFont}
                        fontSize={`${line5Props.fontSize * sizeFactor}px`}
                        fontWeight={line5Props.fontWeight || 400}
                        style={{ minWidth: `${160 * sizeFactor}px`, margin: `${5 * sizeFactor}px 0`, justifyContent: 'left' }}
                    >
                        <span className="anim anim-counter-inc">
                            {data && data.popcorn_amount}
                        </span>
                    </Highlight>

                    <Text className="line-6"
                        color={primaryColor}
                        fontFamily={primaryFont}
                        fontSize={`${line6Props.fontSize * sizeFactor}px`}
                        fontWeight={line5Props.fontWeight || 400}
                        lineHeight='1'
                    >
                        <span className="anim anim-fade-in">palomitas</span>
                    </Text>

                    <div style={{ opacity: current ? 1 : 0 }}>
                        {[1, 2, 3, 4, 5, 6, 7, 1, 2, 3, 4, 5, 6, 7].map((i, index) => (
                            <PopCornImage key={index} index={index} src={asset(`/dist/images/year-recap/popcorn_${i}.png`)} />
                        ))}
                    </div>
                </SlideContent>
            </SlideContainer>
        </RecapSlide>
    );
};